import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "@/store"
import { getPWAThemePrimaryColor } from "@/theme-helper"
import { setTheme } from "@/utils/set-theme"
import { ROLES } from "@/constants/roles"
import { SiteInfo } from "@/types/site-info"

export interface SiteInfoState {
    siteInfo: SiteInfo | null
    isLoading: boolean
    identifier?: string
    showMaintenanceNotification: boolean
}

const state: SiteInfoState = {
    siteInfo: null,
    isLoading: false,
    identifier: undefined,
    showMaintenanceNotification: false,
}

const mutations: MutationTree<SiteInfoState> = {
    setSiteInfo(state, siteInfo: SiteInfo) {
        state.siteInfo = siteInfo
    },
    setIsLoading(state, isLoading) {
        state.isLoading = isLoading
    },
    setIdentifier(state, identifier) {
        state.identifier = identifier
    },
    setShowMaintenanceNotification(state, show) {
        state.showMaintenanceNotification = show
    },
}

const actions: ActionTree<SiteInfoState, RootState> = {
    async fetchSiteInfo({ commit, dispatch }, identifier?: string) {
        commit("setIsLoading", true)

        try {
            let url = "/site/info"

            if (identifier) {
                url += `?identifier=${identifier}`

                commit("setIdentifier", identifier)
            }

            const response = await axios.get(url)
            const siteInfo = response.data.data

            const theme = siteInfo.theme
            setTheme(document.documentElement, theme)

            const bgColor = getPWAThemePrimaryColor(theme, siteInfo.host)

            let themeColorMeta = document.querySelector("meta[name='theme-color']")
            if (themeColorMeta) {
                themeColorMeta.remove()
            }
            themeColorMeta = document.createElement("meta")
            themeColorMeta.setAttribute("name", "theme-color")
            themeColorMeta.setAttribute("content", bgColor)
            document.head.appendChild(themeColorMeta)

            let bgColorMeta = document.querySelector("meta[name='background-color']")
            if (bgColorMeta) {
                bgColorMeta.remove()
            }
            bgColorMeta = document.createElement("meta")
            bgColorMeta.setAttribute("name", "background-color")
            bgColorMeta.setAttribute("content", bgColor)
            document.head.appendChild(bgColorMeta)

            if (!document.body.style.backgroundColor || process.env.NODE_ENV !== "production") {
                document.body.style.backgroundColor = bgColor
            }

            if (theme?.includes("white")) {
                document.body.classList.add("light")
            } else {
                document.body.classList.add("dark")
            }

            const platformName = siteInfo.platformName

            if (platformName) {
                document.title = platformName
            }

            const favicon = siteInfo.favicon

            if (favicon) {
                const faviconLinkElement = document.querySelector(
                    "link[rel~='icon']",
                ) as HTMLLinkElement

                if (faviconLinkElement) {
                    faviconLinkElement.href = favicon
                }
            }

            if (siteInfo.host && process.env.NODE_ENV === "production") {
                const host = siteInfo.host
                axios.defaults.baseURL = `https://${host}/api`
            }

            commit("setSiteInfo", siteInfo)

            dispatch("reduxi/setSiteInfo", siteInfo, { root: true })
        } catch (error) {
            console.error("Error fetching site info:", error)
        } finally {
            commit("setIsLoading", false)
        }
    },
    dismissMaintenanceNotification({ commit }) {
        commit("setShowMaintenanceNotification", false)
    },
    checkMaintenanceNotification({ commit, state }) {
        const isInMaintenance = state.siteInfo?.status === "maintenance"

        if (isInMaintenance) {
            commit("setShowMaintenanceNotification", true)
        }
    },
}

const getters: GetterTree<SiteInfoState, RootState> = {
    siteInfo: (state) => state.siteInfo,
    isLoading: (state) => state.isLoading,
    identifier: (state) => state.identifier,
    productName: (state, _rs, _g, rootGetters) => {
        const user = rootGetters["auth/user"]

        if (user?.role === ROLES.ADMIN) {
            return "OEM"
        }

        return state.siteInfo?.productName || "Reduxi"
    },
    maintenanceStatus: (state) => {
        const siteInfo = state.siteInfo
        return {
            isInMaintenance: siteInfo?.status === "maintenance",
            message: siteInfo?.maintenance?.message || null,
        }
    },
    showMaintenanceNotification: (state) => state.showMaintenanceNotification,
}

const siteInfoModule: Module<SiteInfoState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default siteInfoModule
